import { useRef, useState, useEffect, useContext } from "react";
import AuthContext from "../context/AuthProvider";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "../api/axios";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
const LOGIN_URL = "/auth";
const LOGOUT_URL = "/logout";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const { setAuth } = useAuth();
  const userRef = useRef();
  const errRef = useRef();
  const { auth } = useContext(AuthContext);
  const [name, setName] = useState("");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const axiosPrivate = useAxiosPrivate();
  const [user, setUser] = useState("Guest");
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  useEffect(() => {
    if (userRef.current) userRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [name, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        LOGIN_URL,
        JSON.stringify({ name, password: pwd }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      const accessToken = response?.data?.accessToken;
      const roles = response?.data?.roles;
      setAuth({ user: name, roles, accessToken });
      setName("");
      setPwd("");
      navigate(from, { replace: true });
    } catch (err) {
      if (!err?.response) {
        setErrMsg("No Server Response");
      } else if (err.response?.status === 400) {
        setErrMsg("Missing Username or Password");
      } else if (err.response?.status === 401) {
        setErrMsg("Unauthorized");
      } else {
        setErrMsg("Login Failed");
      }
      errRef.current.focus();
    }
  };
  const handleLogout = async () => {
    try {
      await axiosPrivate.get(LOGOUT_URL, { withCredentials: true });
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setAuth({});
      navigate("/");
    }
  };
  const fetchOrderHistory = async () => {
    try {
      if (!auth?.accessToken) return;
      setIsLoading(true);
      const response = await axiosPrivate.get("/orders/order-history");
      setOrderHistory(response.data);
      setFetchError(null);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (auth?.user) fetchOrderHistory();
  }, []);
  const getUser = async () => {
    if (!auth?.user) return;
    try {
      setIsLoading(true);
      const response = await axiosPrivate.get(`/admin/${auth.user}`);
      setUser(response.data);
      setFetchError(null);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.user) {
      getUser();
    }
  }, [auth]);
  return (
    <main className="login-page">
      <p
        ref={errRef}
        className={errMsg ? "errmsg" : "offscreen"}
        aria-live="assertive"
      >
        {errMsg}
      </p>
      {!auth?.user ? (
        <form onSubmit={handleSubmit} className="form">
          <h2>Sign In</h2>
          <label htmlFor="name">Username:</label>
          <input
            type="text"
            id="name"
            ref={userRef}
            autoComplete="off"
            onChange={(e) => setName(e.target.value)}
            value={name}
            required
          />

          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            onChange={(e) => setPwd(e.target.value)}
            value={pwd}
            required
          />
          <button
            type="button"
            onClick={handleSubmit}
            style={{
              display: "block",
              backgroundColor: "#82853e",
              color: "white",
              border: "none",
              outline: "none",
              padding: "7px",
              borderRadius: "3px",
              margin: "10px auto",
              textDecoration: "none",
            }}
          >
            Sign In
          </button>
          <p>
            Need an Account?
            <br />
            <span className="line">
              <Link to="/register"> Sign Up</Link>
            </span>
          </p>
        </form>
      ) : (
        <div className="userDashboard">
          <div className="user-profile">
            <h2>Profile</h2>

            <p style={{ minHeight: "30px" }}>
              <span style={{ fontWeight: "bold" }}>Name:</span> {user.name}
            </p>
            <p style={{ minHeight: "30px" }}>
              <span style={{ fontWeight: "bold" }}>Email:</span> {user.email}
            </p>
            <p style={{ minHeight: "30px" }}>
              <span style={{ fontWeight: "bold" }}>Number:</span> {user.phone}
            </p>
            <p style={{ minHeight: "30px" }}>
              <span style={{ fontWeight: "bold" }}>Address:</span>{" "}
              {user.address}
            </p>
            <button
              style={{
                width: "100px",
                backgroundColor: "#82853e",
                color: "white",
                border: "none",
                outline: "none",
                padding: "5px",
                borderRadius: "3px",
                margin: "10px auto",
                textDecoration: "none",
                cursor: "pointer",
              }}
              type="button"
              onClick={handleLogout}
            >
              Logout
            </button>
            <p>
              <span className="line">
                <Link to="/admin"> Go to admin dashboard</Link>
              </span>
            </p>
          </div>
          <div className="order-history">
            <h2 style={{ textAlign: "center" }}>Track Your Orders</h2>
            {orderHistory.length > 0 ? (
              <div>
                {orderHistory.map((order) => (
                  <Link
                    to={`/order/${order._id}`}
                    style={{ color: "black", textDecoration: "none" }}
                  >
                    <div
                      className="order-card"
                      style={{ borderBottom: "1px solid #82853e" }}
                      key={order._id}
                    >
                      <p>
                        <strong>Order ID:</strong> {order._id}
                      </p>
                      <p>
                        <strong>Total Amount:</strong> ₹{order.totalAmount}
                      </p>
                      <p>
                        <strong>Status:</strong>{" "}
                        {order.status === 3 && (
                          <span style={{ color: "green", fontWeight: "bold" }}>
                            Delivered
                          </span>
                        )}
                        {order.status === 2 && (
                          <span style={{ color: "amber", fontWeight: "bold" }}>
                            Dispatched
                          </span>
                        )}
                        {order.status === 1 && (
                          <span style={{ color: "Blue", fontWeight: "bold" }}>
                            Processing
                          </span>
                        )}
                        {order.status === 0 && (
                          <span style={{ color: "Gray", fontWeight: "bold" }}>
                            Placed
                          </span>
                        )}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p>No past orders</p>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default Login;
